import debounce from 'lodash/debounce';
import Vue from 'vue';
import { AutoplayStatus, OverlayStatus, PlayerStatus, SubtitlesLanguage, TimelineEventAction } from '@/shared/components/player/enums';
import { ProductType } from '@/shared/enums';
import { services } from '@/shared/plugins/services';
import { storage } from '@/shared/plugins/storage';
import {
  getEpisodes,
  getNextEpisode,
  getPreviousEpisode,
} from '@/shared/utils/episode-helpers';
import { getTrackLanguage } from './store-utils';

const TIME_TO_IDLE = 5000;
const PROFILE_AUTO = {
  name: 'Auto',
  bandwidth: 'auto',
};

export const DISABLE_SUBTITLES = {
  language: SubtitlesLanguage.DISABLED,
};

export const MAX_VOLUME = 100;
export const MIN_VOLUME = 0;

export const INIT_STATE = {
  adBannerVisible: false,
  adsCuePoints: [],
  advertControlsEnabled: undefined,
  autoplayStatus: AutoplayStatus.ALLOWED,
  channelSprites: {},
  controlsEnabled: false,
  currentLiveProgram: {},
  currentProfile: {},
  duration: 0,
  episodes: {},
  eventsEnabled: true,
  eventsOnTimeline: [],
  eventsToShow: [],
  gradualPercentProgress: 0,
  head: 0,
  isBuffering: false,
  isEmbedded: false,
  isLinearAdPlaying: false,
  isPopupVisible: false,
  isSeeking: false,
  mute: storage.mute,
  nextEpisode: {},
  nextProgram: {},
  nextRelatedProduct: {},
  nextRelatedProducts: [],
  options: {
    audioTracks: [],
    profiles: [],
    subtitles: [],
  },
  overlayStatus: OverlayStatus.INTERACTION,
  playerStatus: PlayerStatus.STOPPED,
  previousEpisode: {},
  previousRelatedProduct: {},
  previousRelatedProducts: [],
  product: {},
  progress: 0,
  seasons: {},
  seekRequested: false,
  seekTo: null,
  serial: {},
  settings: {},
  showOptions: false,
  showSeasons: false,
  showVolume: false,
  spriteBaseUrl: '',
  spriteManifest: {},
  subtitles: [],
  timeshiftMode: '',
  volume: storage.volume || MIN_VOLUME,
};

export const state = Vue.observable({ ...INIT_STATE });

const DEFAULT_SETTINGS = {
  audio: {},
  profile: PROFILE_AUTO,
  subtitle: DISABLE_SUBTITLES,
};

export function setIsEmbedded(value = true) {
  state.isEmbedded = value;
}

export function addRelatedProducts(products = []) {
  state.nextRelatedProducts = [...state.nextRelatedProducts, ...products];
}

export function setRelatedProducts(products = []) {
  state.nextRelatedProducts = products;
}

export function setLinearAdPlaying(value) {
  state.isLinearAdPlaying = value;
}

export function setGradualPercentProgress(gradualPercentProgress) {
  state.gradualPercentProgress = gradualPercentProgress;
}

export function setLinearAdsPlaying(value) {
  state.isLinearAdsPlaying = value;
}

export function setDefaultAudioLang(defaultLanguage) {
  DEFAULT_SETTINGS.audio.lang = defaultLanguage.toLowerCase();
}

export function setDefaultPlayerSettings() {
  const LSSettings = getLSSettings();
  state.settings = { ...DEFAULT_SETTINGS, ...LSSettings };
}

export function setSpriteBaseUrl(url) {
  state.spriteBaseUrl = url;
}

export function setSpriteManifest(manifest) {
  state.spriteManifest = manifest;
}

export function addChannelSprite(sprites) {
  state.channelSprites = {
    ...state.channelSprites,
    ...sprites,
  };
}

export function setSeeking(isSeeking) {
  state.isSeeking = isSeeking;
}

export function setAdBannerVisibility(visible) {
  state.adBannerVisible = visible;
}

export function setSeekRequested(seekRequested) {
  state.seekRequested = seekRequested;
}

export function setTimeshiftMode(mode) {
  state.timeshiftMode = mode;
}

export function setAutoplayStatus(status) {
  state.autoplayStatus = status;
}

export function setBuffering(isBuffering) {
  state.isBuffering = isBuffering;
}

export function switchSubtitles(subtitles) {
  state.subtitles = subtitles;
}

export function setPlayerStatus(status) {
  state.playerStatus = status;
}

export function clearPlayerStatus() {
  state.playerStatus = '';
}

export function setControlsEnabled(status) {
  state.controlsEnabled = status;
}

export function setAdvertControlsEnabled(status) {
  state.advertControlsEnabled = status;
}

export function setOverlayStatus(status) {
  state.overlayStatus = status;
}

export function setDuration(seconds) {
  state.duration = seconds;
}

export function setSeekTo(seek) {
  const watchCreditsEvent = state.eventsOnTimeline.find(({ action }) => action === TimelineEventAction.WATCH_CREDITS);
  state.seekTo = seek;

  if (watchCreditsEvent?.since > seek) {
    state.eventsEnabled = true;
  }
}

export function setProgress(seconds) {
  if (typeof seconds !== 'number') {
    return;
  }

  state.progress = seconds;
}

export function setCurrentLiveProgram(program = {}) {
  state.currentLiveProgram = program;
}

export function toggleVolume() {
  if (!state.controlsEnabled && !state.advertControlsEnabled) {
    return;
  }

  state.mute = !state.mute;
  storage.mute = state.mute;

  if (!state.mute) {
    setAutoplayStatus(AutoplayStatus.ALLOWED);
  }
}

export function setMuted(value) {
  state.mute = value;
}

export function setVolume(value) {
  if (!state.controlsEnabled && !state.advertControlsEnabled) {
    return;
  }

  state.volume = Math.max(Math.min(value, MAX_VOLUME), MIN_VOLUME);
  storage.volume = state.volume;

  if ((state.volume && state.mute) || (!state.volume && !state.mute)) {
    toggleVolume();
  }
}

export function toggleSeasons() {
  const show = state.showSeasons;
  closeSubOverlays(show);
  state.showSeasons = !show;
}

export function toggleOptions() {
  const show = state.showOptions;
  closeSubOverlays(show);
  state.showOptions = !show;
}

export function toggleVolumeBar() {
  const show = state.showVolume;
  closeSubOverlays(show);
  state.showVolume = !show;
}

export function isAnySubOverlayOpen() {
  return state.showOptions || state.showSeasons;
}

export function closeSubOverlays(dontClose = false) {
  if (dontClose) {
    return;
  }
  state.showOptions = false;
  state.showSeasons = false;
  state.showVolume = false;
}

export async function setProduct(product) {
  state.product = product;

  if (product.type === ProductType.EPISODE) {
    const season = state.product.season;
    const serialId = season.serial.id;
    const episodes = await getEpisodes(season, serialId);
    state.previousEpisode = await getPreviousEpisode(episodes, product.episode, serialId, season.number) || {};
    state.nextEpisode = await getNextEpisode(episodes, product.episode, serialId, season.number) || {};
  } else if (product.type === ProductType.PROGRAMME) {
    const liveManager = services.resolve('liveManager');
    const programsCollection = liveManager.programs.collection;
    state.nextProgram = programsCollection.findNext(product.till, { id: product.channelId });
  }

  if (state.isEmbedded) {
    state.previousRelatedProduct = state.previousRelatedProducts[state.previousRelatedProducts.length - 1] || {};
    state.nextRelatedProduct = state.nextRelatedProducts[0] || {};
  }
}

export function addEventOnTimeline(event) {
  state.eventsOnTimeline.push(event);
}

export function setCurrentEventOnTimeline(time) {
  if (state.overlayStatus === OverlayStatus.WATCH_CREDITS) {
    return;
  }

  if (!state.eventsEnabled) {
    return;
  }

  state.eventsToShow = state.eventsOnTimeline
    .filter(({ since, till = time }) => {
      return time >= since && time <= till;
    });
}

export function clearEvents() {
  state.eventsToShow = [];
  state.eventsEnabled = false;
  setOverlayStatus(OverlayStatus.WATCH_CREDITS);
}

export const setIdle = debounce(() => {
  const { overlayStatus, showOptions, showSeasons } = state;

  if (overlayStatus === OverlayStatus.WATCH_CREDITS) {
    return;
  }

  if (showOptions || showSeasons) {
    return;
  }

  closeSubOverlays();
  setOverlayStatus(OverlayStatus.IDLE);
}, TIME_TO_IDLE);

export function setProfile({ height, bandwidth, name }) {
  state.settings.profile = { height, bandwidth, name };
}

export function setHead(head) {
  state.head = head;
}

export function setAudio(track) {
  state.settings.audio = track;
}

export function setAudioTracks(audioTracks) {
  state.options.audioTracks = audioTracks;
}

export function setFallbackAudioTrack(audioTracks = state.options.audioTracks) {
  const audio = audioTracks.find((audioTrack) => getTrackLanguage(audioTrack) === DEFAULT_SETTINGS.audio.lang) || audioTracks[0];

  setAudio(audio);
}

export function setSubtitle(subtitle) {
  state.settings.subtitle = subtitle;
}

export function setSubtitles(subtitles = []) {
  state.options.subtitles = [...subtitles, DISABLE_SUBTITLES];
}

export function setFallbackSubtitle(subtitles = state.options.subtitles) {
  const languageFromStorage = storage.playerSubtitle?.language;
  const subtitle = subtitles.find(({ language }) => language === (languageFromStorage || DEFAULT_SETTINGS.audio.lang));

  setSubtitle(subtitle || DISABLE_SUBTITLES);
}

export function setProfiles(profiles = []) {
  profiles = profiles.filter((profile) => {
    return profile.height || profile.name || profile.bandwidth;
  });
  state.options.profiles = [...profiles, PROFILE_AUTO];
}

function getLSSettings() {
  const settings = {};
  const audio = storage.playerAudio;
  const profile = storage.playerProfile;
  const subtitle = storage.playerSubtitle;

  if (audio) {
    settings.audio = audio;
  }

  if (profile) {
    settings.profile = profile;
  }

  if (subtitle) {
    settings.subtitle = subtitle;
  }

  return settings;
}

export function setInitState() {
  state.advertControlsEnabled = undefined;
  state.autoplayStatus = AutoplayStatus.ALLOWED;
  state.currentLiveProgram = {};
  state.currentProfile = {};
  state.duration = 0;
  state.controlsEnabled = false;
  state.episodes = {};
  state.eventsEnabled = true;
  state.eventsOnTimeline = [];
  state.eventsToShow = [];
  state.isLinearAdPlaying = false;
  state.head = 0;
  state.nextEpisode = {};
  state.nextProgram = {};
  state.overlayStatus = OverlayStatus.INTERACTION;
  state.options = { audioTracks: [], profiles: [], subtitles: [] };
  state.playerStatus = PlayerStatus.STOPPED;
  state.previousEpisode = {};
  state.product = {};
  state.progress = 0;
  state.seasons = {};
  state.serial = {};
  state.showSeasons = false;
  state.spriteBaseUrl = '';
  state.spriteManifest = {};
  state.channelSprites = {};
  state.subtitles = [];
  state.timeshiftMode = '';
}
